import type { ReactNode } from 'react';

import { Icon } from './Icon';

export interface LanguageButtonProps {
  code: string;
  nativeName: string;
  englishName?: string;
  active?: boolean;
  disabled?: boolean;
  href: string;
  children?: ReactNode;
}

export function LanguageButton({
  code,
  nativeName,
  englishName,
  active = false,
  disabled = false,
  href,
}: LanguageButtonProps) {
  const classes = [
    'relative flex w-full items-center gap-3 px-[18px] py-[15px] mb-3 text-left',
    'rounded-[var(--radius-lg)] border no-underline',
    'transition-[transform,box-shadow,background-color,border-color]',
    'duration-[var(--duration-base)] ease-[var(--ease-brand)]',
    active
      ? 'border-transparent bg-plum-700 text-white shadow-[var(--shadow-md)]'
      : 'border-ink-200 bg-paper shadow-[var(--shadow-sm)] hover:-translate-y-px hover:shadow-[var(--shadow-md)]',
    disabled ? 'pointer-events-none opacity-50' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (disabled) {
    return (
      <span className={classes} aria-disabled="true">
        <LanguageLabel nativeName={nativeName} englishName={englishName} active={active} />
      </span>
    );
  }

  return (
    <a
      href={href}
      lang={code}
      hrefLang={code}
      aria-current={active ? 'true' : undefined}
      className={classes}
    >
      <LanguageLabel nativeName={nativeName} englishName={englishName} active={active} />
    </a>
  );
}

function LanguageLabel({
  nativeName,
  englishName,
  active,
}: {
  nativeName: string;

  englishName: string | undefined;
  active: boolean;
}) {
  return (
    <>
      <span
        className={[
          'text-[17px] font-semibold',
          active ? 'text-white' : 'text-ink-900',
        ].join(' ')}
      >
        {nativeName}
      </span>

      
      {!active && englishName ? (
        <span className="ml-auto text-[13px] text-ink-500">{englishName}</span>
      ) : (
        <span className={active ? 'ml-auto text-white/80' : 'ml-auto text-ink-300'}>
          <Icon name="chevron-right" size={18} />
        </span>
      )}
    </>
  );
}
